import { BoundTextAreaField, BoundTextField, Css, FormSectionLayout, useModal } from "@homebound/beam";
import { ObjectConfig, useFormState } from "@homebound/form-state";
import { AssetUppyFile } from "src/components/uploader/UppyUploader";
import { UploaderProvider } from "src/contexts/UploaderContext";
import { ReadyPlanAssetType, SavePlanPackageInput, SaveReadyPlanLocationInput } from "src/generated/graphqlTypes";
import { useNavigateToRpav } from "src/hooks/useNavigateToRpav";
import { disableBasedOnPotentialOperation } from "src/routes/components/PotentialOperationsUtils";
import { AddInsideMapsRoomsModal } from "src/routes/libraries/plan-package-v2/components/checkout/AddInsideMapsRoomsModal";
import { InsideMapsRoomsTable } from "src/routes/libraries/plan-package-v2/components/checkout/InsideMapsRoomsTable";
import {
  PlanPackageCheckoutV2_PlanPackageFragment,
  usePlanPackageCheckoutV2Query,
  useSavePlanPackageCheckoutRoomsV2Mutation,
  useSavePlanPackageCheckoutV2Mutation,
} from "src/routes/libraries/plan-package-v2/components/CheckoutTab.generated";
import { PlanPackageAssetUploader } from "src/routes/libraries/plan-package-v2/components/PlanPackageAssetUploader";
import type { PlanPackageTabProps } from "src/routes/libraries/plan-package-v2/PlanPackagePageV2";
import { queryResult } from "src/utils";

export function CheckoutTab(props: PlanPackageTabProps) {
  const { planPackageId, versionId } = props;
  const query = usePlanPackageCheckoutV2Query({ variables: { id: planPackageId, versionId } });
  return queryResult(query, ({ planPackage }) => <DataView planPackage={planPackage} />);
}

type DataViewProps = { planPackage: PlanPackageCheckoutV2_PlanPackageFragment };

function DataView(props: DataViewProps) {
  const { planPackage } = props;
  const navigateToRpav = useNavigateToRpav();
  const { openModal } = useModal();
  const [save] = useSavePlanPackageCheckoutV2Mutation();
  const [saveLocations] = useSavePlanPackageCheckoutRoomsV2Mutation();

  const formState = useFormState({
    config: checkoutConfig,
    init: { input: planPackage, map: ({ description, insideMapsProjectId }) => ({ description, insideMapsProjectId }) },
    autoSave: (state) => savePlanPackage(state.changedValue),
  });

  async function savePlanPackage(input: Omit<SavePlanPackageInput, "id">) {
    const { data } = await save({ variables: { input: { id: planPackage.id, ...input } } });
    const saved = data?.savePlanPackage.planPackage;
    if (saved && saved.version.id !== planPackage.version.id) {
      // Saving a published version bumped us to a new draft, so clear dirty fields before navigating to it.
      formState.update(saved);
      navigateToRpav(saved.version.id);
    }
  }

  async function saveRpls(inputs: SaveReadyPlanLocationInput[]) {
    const { data } = await saveLocations({ variables: { inputs } });
    const draftId = data?.saveReadyPlanLocations.readyPlanLocations.first?.readyPlan.aggregateDraft?.id;
    if (draftId && draftId !== planPackage.version.id) navigateToRpav(draftId);
  }

  function saveFloorPlan(type: ReadyPlanAssetType, file: AssetUppyFile | undefined) {
    const existing = planPackage.assets.findLast((rpa) => rpa.type.code === type);
    // No file means we are removing the floor plan
    const inputs = !file
      ? planPackage.assets
          .filter((rpa) => rpa.active && rpa.type.code === type)
          .map((rpa) => ({ id: rpa.id, active: false }))
      : [
          {
            ...(existing ? { id: existing.id } : { type }),
            active: true,
            asset: {
              id: null,
              contentType: file.type,
              fileName: file.name,
              s3Key: file.meta.s3Key as string,
              fileSizeInBytes: file.data?.size ?? file.size ?? 0,
            },
          },
        ];
    void savePlanPackage({ assets: inputs });
  }

  const { maxInsideMapsRooms } = planPackage;
  const disabled = disableBasedOnPotentialOperation(planPackage.canEdit);
  const checkoutRooms = planPackage.roomLocations
    .filter((rpl) => rpl.isInsideMapsRoom)
    .sortBy((rpl) => rpl.insideMapsRoomOrder ?? 0);
  const eligibleRooms = planPackage.roomLocations
    .filter((rpl) => !rpl.isInsideMapsRoom)
    .sortBy((rpl) => rpl.location.name);
  const addDisabled =
    disabled ||
    (checkoutRooms.length >= maxInsideMapsRooms && `Checkout supports up to ${maxInsideMapsRooms} rooms`) ||
    (eligibleRooms.isEmpty && "All of the plan's rooms have been added");

  return (
    <UploaderProvider>
      <FormSectionLayout
        title="Checkout Configuration"
        sections={[
          {
            title: "Sales Description",
            description: "Enter customer facing content shown in Checkout.",
            fields: (
              <BoundTextAreaField
                label="Sales Description"
                field={formState.description}
                fullWidth
                disabled={disabled}
              />
            ),
          },
          {
            title: "InsideMaps Configuration",
            description: "Link this plan to its InsideMaps project.",
            fields: (
              <BoundTextField
                label="InsideMaps Project ID"
                field={formState.insideMapsProjectId}
                fullWidth
                disabled={disabled}
              />
            ),
          },
          {
            title: "InsideMaps Room Configuration",
            description: "Control which rooms appear in checkout. Locations are only editable on the locations tab.",
            actions: [
              {
                label: "Add",
                icon: "plus",
                variant: "secondary",
                disabled: addDisabled,
                onClick: () =>
                  openModal({
                    content: (
                      <AddInsideMapsRoomsModal
                        rooms={checkoutRooms}
                        eligibleRooms={eligibleRooms}
                        maxRooms={maxInsideMapsRooms}
                        saveRpls={saveRpls}
                      />
                    ),
                  }),
              },
            ],
            fields: <InsideMapsRoomsTable rooms={checkoutRooms} disabled={disabled} saveRpls={saveRpls} />,
          },
          {
            title: "Floor Plans",
            description: "Upload the floor plan SVGs that will be shown in Checkout.",
            fields: (
              <>
                {floorPlans.map(({ label, type }) => (
                  <div key={type}>
                    <div css={Css.sm.gray700.mbPx(4).$}>{label}</div>
                    <PlanPackageAssetUploader
                      assetUrl={
                        planPackage.assets.findLast((rpa) => rpa.active && rpa.type.code === type)?.asset.downloadUrl
                      }
                      saveAsset={(file) => saveFloorPlan(type, file)}
                      assetType={type}
                      disabled={disabled}
                    />
                  </div>
                ))}
              </>
            ),
          },
        ]}
      />
    </UploaderProvider>
  );
}

const floorPlans = [
  { label: "Plan Floor Plan", type: ReadyPlanAssetType.FloorPlan },
  { label: "Ground/Basement Floor Plan", type: ReadyPlanAssetType.GroundBasementFloorPlan },
  { label: "First Floor Plan", type: ReadyPlanAssetType.FirstFloorPlan },
  { label: "Second Floor Plan", type: ReadyPlanAssetType.SecondFloorPlan },
  { label: "ADU First Floor Plan", type: ReadyPlanAssetType.AduFirstFloorPlan },
  { label: "ADU Second Floor Plan", type: ReadyPlanAssetType.AduSecondFloorPlan },
] as const;

type CheckoutForm = Pick<SavePlanPackageInput, "description" | "insideMapsProjectId">;

const checkoutConfig: ObjectConfig<CheckoutForm> = {
  description: { type: "value" },
  insideMapsProjectId: { type: "value" },
};
